import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getCategories } from '../data/wordService';
import './Selector.css';

function CategorySelector({ selectedCategories, onCategoryChange }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectorRef = useRef(null);
  const categories = getCategories().filter(cat => cat.value !== 'all'); // Remove 'all' option

  const handleCategoryToggle = (categoryValue) => {
    const newSelection = selectedCategories.includes(categoryValue)
      ? selectedCategories.filter(cat => cat !== categoryValue)
      : [...selectedCategories, categoryValue];

    onCategoryChange(newSelection);
  };

  const handleSelectAll = () => {
    const allCategoryValues = categories.map(cat => cat.value);
    onCategoryChange(allCategoryValues);
  };

  const handleClearAll = () => {
    onCategoryChange([]);
  };

  const getDisplayText = () => {
    if (selectedCategories.length === 0) return 'No categories selected';
    if (selectedCategories.length === categories.length) return 'All Categories';
    if (selectedCategories.length === 1) {
      const category = categories.find(cat => cat.value === selectedCategories[0]);
      return category?.label || 'Unknown';
    }
    return `${selectedCategories.length} Categories`;
  };

  const handleToggleExpanded = () => {
    if (isExpanded) {
      // Closing - check if nothing is selected and default to all categories
      if (selectedCategories.length === 0) {
        const allCategoryValues = categories.map(cat => cat.value);
        onCategoryChange(allCategoryValues);
      }
    }
    setIsExpanded(!isExpanded);
  };

  const closeDropdown = useCallback(() => {
    if (isExpanded) {
      // Check if nothing is selected and default to all categories
      if (selectedCategories.length === 0) {
        const allCategoryValues = categories.map(cat => cat.value);
        onCategoryChange(allCategoryValues);
      }
      setIsExpanded(false);
    }
  }, [isExpanded, selectedCategories, categories, onCategoryChange]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded, selectedCategories]);

  return (
    <div className="selector">
      <label className="selector-label">Categories:</label>

      <div className="category-selector" ref={selectorRef}>
        <button
          className="category-toggle-btn"
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
        >
          {getDisplayText()}
          <span className={`arrow ${isExpanded ? 'up' : 'down'}`}>▼</span>
        </button>

        {isExpanded && (
          <div className="category-dropdown">
            <div className="category-actions">
              <button
                className="category-action-btn"
                onClick={handleSelectAll}
                disabled={selectedCategories.length === categories.length}
              >
                Select All
              </button>
              <button
                className="category-action-btn"
                onClick={handleClearAll}
                disabled={selectedCategories.length === 0}
              >
                Clear All
              </button>
            </div>

            <div className="category-options">
              {categories.map((category) => (
                <label key={category.value} className="category-option">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.value)}
                    onChange={() => handleCategoryToggle(category.value)}
                    className="category-checkbox"
                  />
                  <span className="category-label-text">{category.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CategorySelector;